import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, ChevronLeft, Home, LogIn, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import ddLogo from '../components/ui/dd.png';

const UnauthorizedPage = () => {
  const { theme } = useTheme();

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Stage Background - matching competition portal */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-red-900/20 to-orange-900/20"></div>
      
      {/* Animated background particles */}
      <div className="absolute inset-0">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-red-500/30 rounded-full"
            animate={{
              x: [0, 100, 0],
              y: [0, -100, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>
      
      {/* Main content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="max-w-xl w-full"
        >
          {/* Header with logo */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center mb-6"
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="relative"
              >
                <img 
                  src={ddLogo}
                  alt="Dance Digital"
                  className="w-20 h-20 object-contain relative z-10"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-orange-500 rounded-2xl blur-xl opacity-60"></div>
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-400/40 text-base text-red-200 mb-8 backdrop-blur-xl"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              <span className="font-semibold">Organization Security System</span>
            </motion.div>
          </div>

          {/* Main card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="relative group"
          >
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/30 to-orange-500/30 rounded-3xl blur-xl opacity-40"></div>
            
            {/* Main card */}
            <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-red-400/30 p-8 shadow-2xl shadow-red-500/10">
              <div className="flex flex-col items-center text-center">
                {/* Icon */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-400/30 backdrop-blur-sm"
                >
                  <ShieldAlert className="w-10 h-10 text-red-400" />
                </motion.div>
                
                {/* Title */}
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1 }}
                  className="text-3xl md:text-4xl font-black mb-4"
                >
                  <span className="bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
                    ACCESS DENIED
                  </span>
                </motion.h1>
                
                {/* Description */}
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                  className="text-lg text-white/80 font-medium leading-relaxed mb-8 max-w-md"
                >
                  You don't have permission to access this competition. This area is restricted to the 
                  <span className="text-red-300 font-bold"> organization owner</span> and their 
                  <span className="text-orange-300 font-bold"> authorized personnel</span> only.
                </motion.p>
                
                {/* Action buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.4 }}
                  className="flex flex-col sm:flex-row gap-4 w-full"
                >
                  <Link
                    to="/competition/portal"
                    className="flex-1 group relative overflow-hidden rounded-xl bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-sm border border-white/20 px-6 py-3 transition-all duration-300 hover:border-white/30 hover:bg-white/15 hover:scale-105"
                  >
                    <div className="flex items-center justify-center">
                      <Home className="w-5 h-5 mr-2 text-white/80 group-hover:text-white transition-colors" />
                      <span className="font-semibold text-white/90 group-hover:text-white transition-colors">
                        Competition Portal
                      </span>
                    </div>
                  </Link>
                  
                  <Link
                    to="/auth?state=signIn"
                    className="flex-1 group relative overflow-hidden rounded-xl bg-gradient-to-r from-red-500/80 to-orange-500/80 px-6 py-3 transition-all duration-300 hover:from-red-500 hover:to-orange-500 hover:scale-105 shadow-lg hover:shadow-red-500/25"
                  >
                    <div className="flex items-center justify-center">
                      <LogIn className="w-5 h-5 mr-2 text-white group-hover:scale-110 transition-transform" />
                      <span className="font-semibold text-white">
                        Sign In
                      </span>
                    </div>
                  </Link>
                </motion.div>
                
                {/* Help text */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.6 }}
                  className="text-sm text-white/60 mt-6 leading-relaxed"
                >
                  Need access? Contact the competition owner or sign in with an account from the organization that created this competition.
                </motion.p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default UnauthorizedPage; 